import {create} from 'zustand';
import {createJSONStorage, devtools, persist} from 'zustand/middleware';
import {
  Person,
  PersonExtended,
  ContactDetailsValidation,
  WeddingOccasionDB,
  WeddingOfficiantDB,
} from '../lib/interfaces';
import {Language} from '../lib/enums';

export interface BookingState {
  selectedMonthYear: string;
  weddingOccasionId: string;
  person1: Person;
  person2: Person;
  ceremonyLanguage: string;
  weddingOfficiant: string;
  weddingOccasionTime: string;
  location: string;
  validationFields: ContactDetailsValidation[];
  customOccasionAddress: string;
  customOccasionCity: string;
  weddingOfficiantId: string;
  isCustomOccasion: boolean;
  clearState: () => void;
  setPerson: (person: Person, personNumber: number) => void;
  setSelectedMonthYear: (selectedMonthYear: string) => void;
  setWeddingOccasionId: (selectedWeddingOccasionId: string) => void;
  setWeddingOccasion: (weddingOccasion: WeddingOccasionDB | null) => void;
  updatePersonProperty: (personNo: number, propertyKey: string, value: string | number | boolean) => void;
  updatePersonExtendedProperty: (personNo: number, propertyKey: string, value: string | number | boolean) => void;
  resetPersonExtended: (personNo: number) => void;
  setCeremonyLanguage: (ceremonyLanguage: string) => void;
  setValidationFields: (validationFields: ContactDetailsValidation[]) => void;
  setCustomOccasion: (address: string, city: string, officiant: WeddingOfficiantDB, time: string) => void;
  setIsCustomOccasion: (isCustomOccasion: boolean) => void;
}

const defaultValues = {
  selectedMonthYear: '',
  weddingOccasionId: '',
  person1: {personExtended: undefined} as Person,
  person2: {personExtended: undefined} as Person,
  ceremonyLanguage: Language.Swedish,
  weddingOfficiant: '',
  weddingOccasionTime: '',
  location: '',
  validationFields: [],
  customOccasionAddress: '',
  customOccasionCity: '',
  weddingOfficiantId: '',
  isCustomOccasion: false,
};

export const useBookingState = create<BookingState>()(
  devtools(
    persist(
      (set) => ({
        ...defaultValues,
        clearState: () =>
          set({
            ...defaultValues,
          }),
        setSelectedMonthYear: (selectedMonthYear: string) =>
          set({selectedMonthYear: selectedMonthYear, weddingOccasionId: ''}),
        setWeddingOccasionId: (selectedWeddingOccasionId: string) =>
          set({weddingOccasionId: selectedWeddingOccasionId}),
        setWeddingOccasion: (wo: WeddingOccasionDB | null) =>
          wo
            ? set({
                weddingOccasionId: wo.id,
                weddingOfficiant: wo.weddingOfficiant.name,
                weddingOccasionTime: wo.time,
                location: `${wo.location.name}, ${wo.location.address}`,
              })
            : set({
                weddingOccasionId: '',
                weddingOfficiant: '',
                weddingOccasionTime: '',
                location: '',
              }),
        setPerson: (person: Person, personNumber: number) => set({['person' + personNumber]: person}),
        updatePersonProperty: (personNumber: number, propertyKey: string, value: string | number | boolean) => {
          switch (personNumber) {
            case 1:
              set((state) => ({
                person1: {
                  ...state.person1,
                  [propertyKey]: value,
                },
              }));
              break;
            case 2:
              set((state) => ({
                person2: {
                  ...state.person2,
                  [propertyKey]: value,
                },
              }));
              break;
          }
        },
        setCeremonyLanguage: (ceremonyLanguage: string) => set({ceremonyLanguage: ceremonyLanguage}),
        updatePersonExtendedProperty: (personNumber: number, propertyKey: string, value: string | number | boolean) => {
          switch (personNumber) {
            case 1:
              set((state) => ({
                person1: {
                  ...state.person1,
                  personExtended: {
                    ...state.person1.personExtended,
                    [propertyKey]: value,
                  } as PersonExtended,
                },
              }));
              break;
            case 2:
              set((state) => ({
                person2: {
                  ...state.person2,
                  personExtended: {
                    ...state.person2.personExtended,
                    [propertyKey]: value,
                  } as PersonExtended,
                },
              }));
              break;
          }
        },
        resetPersonExtended: (personNumber: number) => {
          switch (personNumber) {
            case 1:
              set((state) => ({
                person1: {
                  ...state.person1,
                  personExtended: undefined,
                },
                validationFields: state.validationFields.filter((field) =>
                  ['personalIdentityNumber', 'phone', 'email'].includes(field.nameKey)
                ),
              }));
              break;
            case 2:
              set((state) => ({
                person2: {
                  ...state.person2,
                  personExtended: undefined,
                },
                validationFields: state.validationFields.filter((field) =>
                  ['personalIdentityNumber', 'phone', 'email'].includes(field.nameKey)
                ),
              }));
              break;
          }
        },
        setValidationFields: (validationFields: ContactDetailsValidation[]) => set({validationFields}),
        setCustomOccasion: (address, city, officiant, time) =>
          set({
            customOccasionAddress: address,
            weddingOfficiantId: officiant.id,
            weddingOfficiant: officiant.name,
            customOccasionCity: city,
            weddingOccasionTime: time,
            location: `${address}, ${city}`,
            isCustomOccasion: true,
          }),
        setIsCustomOccasion: (isCustomOccasion: boolean) => set({isCustomOccasion}),
      }),
      {
        name: 'booking-storage',
        storage: createJSONStorage(() => sessionStorage), // Uses session storage, remove this to use local storage by default
      }
    )
  )
);
